// Timezone helpers.
//
// IANA timezone identifiers (e.g. "Australia/Sydney") are the single canonical
// representation. Fixed UTC offsets (e.g. "UTC+10") are NEVER stored because
// daylight-saving rules change over time and vary by region; a zone identifier
// lets the runtime compute the correct offset for any instant, past or future.

/**
 * Static fallback list used only when the runtime does not expose
 * `Intl.supportedValuesOf`. It covers Australian/NZ zones plus the most common
 * global zones. The primary path (modern browsers / Node 18+) returns the full
 * IANA set, which is far larger.
 */
const FALLBACK_ZONES: string[] = [
  "UTC",
  // Australia / New Zealand
  "Australia/Sydney",
  "Australia/Melbourne",
  "Australia/Brisbane",
  "Australia/Adelaide",
  "Australia/Darwin",
  "Australia/Perth",
  "Australia/Hobart",
  "Australia/Canberra",
  "Australia/Lord_Howe",
  "Australia/Eucla",
  "Pacific/Auckland",
  "Pacific/Chatham",
  "Pacific/Norfolk",
  "Pacific/Fiji",
  "Pacific/Honolulu",
  // Americas
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Phoenix",
  "America/Anchorage",
  "America/Toronto",
  "America/Vancouver",
  "America/Sao_Paulo",
  "America/Mexico_City",
  // Europe
  "Europe/London",
  "Europe/Dublin",
  "Europe/Paris",
  "Europe/Berlin",
  "Europe/Madrid",
  "Europe/Rome",
  "Europe/Amsterdam",
  "Europe/Zurich",
  "Europe/Stockholm",
  "Europe/Oslo",
  "Europe/Athens",
  // Asia
  "Asia/Tokyo",
  "Asia/Seoul",
  "Asia/Shanghai",
  "Asia/Hong_Kong",
  "Asia/Singapore",
  "Asia/Kolkata",
  "Asia/Dubai",
  "Asia/Jakarta",
  "Asia/Bangkok",
  // Africa
  "Africa/Johannesburg",
  "Africa/Cairo",
  "Africa/Nairobi",
];

/** The full list of IANA timezone identifiers, sorted alphabetically. */
export function getTimezones(): string[] {
  const supported = (
    Intl as unknown as { supportedValuesOf?: (key: string) => string[] }
  ).supportedValuesOf;
  if (typeof supported === "function") {
    try {
      const zones = supported("timeZone");
      if (Array.isArray(zones) && zones.length > 0) {
        return [...zones].sort();
      }
    } catch {
      // Fall through to the static list.
    }
  }
  return [...FALLBACK_ZONES].sort();
}

/** Detect the browser/device timezone as an IANA identifier, if available. */
export function detectBrowserTimezone(): string | null {
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return zone && zone.trim() !== "" ? zone : null;
  } catch {
    return null;
  }
}

/** Whether `zone` is a known IANA identifier in the current runtime. */
export function isKnownTimezone(zone: string): boolean {
  if (zone == null || zone.trim() === "") return false;
  // Bare "UTC" is the platform default. Some runtimes omit it from
  // Intl.supportedValuesOf("timeZone") while still accepting it on DateTimeFormat.
  if (zone === "UTC") return true;
  return getTimezones().includes(zone);
}

/**
 * Human-readable label for an IANA identifier, e.g.
 *   "Australia/Sydney" -> "Sydney (Australia)"
 *   "America/Argentina/Buenos_Aires" -> "Buenos Aires (Argentina)"
 *   "UTC" -> "UTC"
 */
export function formatTimezoneLabel(zone: string): string {
  if (!zone) return "";
  const parts = zone.split("/");
  if (parts.length <= 1) return zone.replace(/_/g, " ");
  const city = parts[parts.length - 1].replace(/_/g, " ");
  const region = parts[parts.length - 2].replace(/_/g, " ");
  return `${city} (${region})`;
}

/** Current UTC offset label for a zone (e.g. "GMT+10" or "GMT+10:30"). */
export function timezoneOffsetLabel(zone: string): string {
  try {
    const fmt = new Intl.DateTimeFormat("en-US", {
      timeZone: zone,
      timeZoneName: "shortOffset",
    });
    const part = fmt.formatToParts(new Date()).find((p) => p.type === "timeZoneName");
    return part?.value ?? "";
  } catch {
    return "";
  }
}

/**
 * Resolve the timezone to show in a form.
 *
 * - An existing, valid IANA value is always preserved (never silently
 *   overwritten, including the bare "UTC" default).
 * - Only when nothing has been configured (empty/null/unknown) do we fall back
 *   to the detected device timezone, then "UTC".
 */
export function resolveInitialTimezone(existing?: string | null): string {
  if (existing && existing.trim() !== "" && isKnownTimezone(existing)) {
    return existing;
  }
  const detected = detectBrowserTimezone();
  if (detected && isKnownTimezone(detected)) return detected;
  return "UTC";
}